import React from "react";
import { Box, Paper, Skeleton } from "@mui/material";
import CreateSectionHeader from "../CreateNotice/CreateSectionHeader";
import {
  CERTIFICATES_CRUMB,
  CERTIFICATE_TEMPLATES_CRUMB,
} from "./certificateCrumbs";
import { CERTIFICATE_FITS_SCREEN_MAX_WIDTH } from "./certificateStyles";
import { certificatePageSize } from "../../domain/certificates/certificateTemplates";

const { width, height } = certificatePageSize("landscape");

/** A grey block the shape of a landscape certificate. */
const CertificateBlock: React.FC = () => (
  <Skeleton
    variant="rectangular"
    height="auto"
    sx={{ width: "100%", aspectRatio: `${width} / ${height}` }}
  />
);

/** A grey settings column beside a grey certificate, like the editor and an Award's details. */
export const SettingsBesideCertificateSkeleton: React.FC = () => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "320px minmax(0, 1fr)" },
      gap: 3,
      alignItems: "start",
    }}
  >
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
      {[0, 1, 2, 3, 4].map((field) => (
        <Skeleton key={field} variant="rounded" height={40} sx={{ mb: 2 }} />
      ))}
    </Paper>
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2, p: { xs: 2, sm: 3 }, bgcolor: "background.default" }}
    >
      <CertificateBlock />
    </Paper>
  </Box>
);

/**
 * Shown while a certificate template loads: its header straight away, and grey
 * blocks where the settings and certificate will be. A ready-made one has no
 * settings, so only the certificate is shown.
 */
const CertificateTemplateSkeleton: React.FC<{
  title: string;
  withSettings: boolean;
}> = ({ title, withSettings }) => (
  <>
    <CreateSectionHeader
      title={title}
      crumbs={[CERTIFICATES_CRUMB, CERTIFICATE_TEMPLATES_CRUMB]}
    />
    <Box role="status" aria-label="Loading certificate template">
      {withSettings ? (
        <SettingsBesideCertificateSkeleton />
      ) : (
        <Paper
          variant="outlined"
          sx={{ borderRadius: 2, p: { xs: 2, sm: 4 }, bgcolor: "background.default" }}
        >
          <Box sx={{ maxWidth: CERTIFICATE_FITS_SCREEN_MAX_WIDTH, mx: "auto" }}>
            <CertificateBlock />
          </Box>
        </Paper>
      )}
    </Box>
  </>
);

export default CertificateTemplateSkeleton;
